import {
  Alert,
  Box,
  Button,
  Checkbox,
  MenuItem,
  Link as MuiLink,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Link, useParams } from 'react-router';
import ActionConfirmDialog from '~/components/Commons/ActionConfirmDialog';
import DianomiSpinner from '~/components/Commons/DianomiSpinner/DianomiSpinner';
import useNotifications from '~/context/Notifications';
import {
  useAssignParentChildReportTemplatesMutation,
  useUnassignParentChildReportTemplatesMutation,
} from '~/hooks/mutations/useParentChildReportTemplateAssignMutations';
import {
  useAssignStaffClientReportTemplatesMutation,
  useUnassignStaffClientReportTemplatesMutation,
} from '~/hooks/mutations/useStaffClientReportTemplateAssignMutations';
import { analyticsStatMetadataQueryOptions } from '~/hooks/queries/useAnalyticsStatMetadataQuery';
import {
  getAnalyticsStatsViewsQueryOptions,
  isAssignedTemplate,
} from '~/hooks/queries/useAnalyticsStatsViewsQuery';
import { useParentChildReportTemplatesQuery } from '~/hooks/queries/useParentChildReportTemplatesQuery';
import { useStaffClientReportTemplatesQuery } from '~/hooks/queries/useStaffClientReportTemplatesQuery';
import { prefetchQueryNonBlocking } from '~/routing/prefetchQueryNonBlocking';
import { ErrorStateCard } from '~/views/Dashboard/AnalyticsStats/AnalyticsStatsShared';
import { ReportAccessLossWarning } from './ReportAccessLossWarning';
import { useStatAccessMode } from './StatAccessModeContext';
import {
  getBulkTemplateFailureMessages,
  summarizeBulkTemplateResults,
} from './summarizeBulkTemplateResults';
import { useStaffAnalyticsConfigMode } from './StaffAnalyticsConfigModeContext';

function templateDisplayName(name: string | null | undefined): string {
  return name ?? 'Untitled report';
}

function getAssignabilityLabel(assignability: {
  alreadyAssigned: boolean;
  hasAccountLimit: boolean;
  canAssign?: boolean;
  code?: string;
}): string | undefined {
  if (assignability.alreadyAssigned) {
    return 'Already assigned';
  }
  if (!assignability.hasAccountLimit) {
    return 'No client access for this stat';
  }
  if (assignability.canAssign === false) {
    return 'Wrong reporting dataset';
  }
  return undefined;
}

function StaffAssignFromLibrarySection({
  accountType,
  accountId,
}: {
  accountType: 'client' | 'partner';
  accountId: string;
}) {
  const [statKeyFilter, setStatKeyFilter] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const { setTimedNotification } = useNotifications();
  const assignMutation = useAssignStaffClientReportTemplatesMutation();
  const { configQuery } = useStaffAnalyticsConfigMode();
  const analyticsReportingMode = configQuery?.analyticsReportingMode ?? null;

  const templatesQuery = useStaffClientReportTemplatesQuery(
    accountType,
    accountId,
    statKeyFilter || undefined,
    analyticsReportingMode,
  );
  const allTemplatesQuery = useStaffClientReportTemplatesQuery(
    accountType,
    accountId,
    undefined,
    analyticsReportingMode,
  );
  const templates = templatesQuery.data?.templates ?? [];
  const statKeyOptions = React.useMemo(
    () => [...new Set((allTemplatesQuery.data?.templates ?? []).map((t) => t.statKey))].sort(),
    [allTemplatesQuery.data?.templates],
  );

  const selectableIds = templates.filter((t) => t.assignability.canAssign).map((t) => t.publicId);

  const handleStatKeyFilterChange = (value: string) => {
    setStatKeyFilter(value);
    setSelected(new Set());
  };

  const toggle = (publicId: string, canAssign: boolean) => {
    if (!canAssign) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected(new Set(selectableIds));
  };

  const handleAssign = () => {
    const templatePublicIds = [...selected];
    if (templatePublicIds.length === 0) return;
    assignMutation.mutate(
      { accountType, accountId, templatePublicIds, analyticsReportingMode },
      {
        onSuccess: (data) => {
          setSelected(new Set());
          const summary = summarizeBulkTemplateResults(data.results, 'assign');
          setTimedNotification('success', summary, 6000);
          const failures = getBulkTemplateFailureMessages(data.results);
          if (failures.length > 0) {
            setTimedNotification('danger', failures.join(' '), 8000);
          }
        },
        onError: (err) => {
          setTimedNotification(
            'danger',
            err instanceof Error ? err.message : 'Assign failed',
            5000,
          );
        },
      },
    );
  };

  const individualStatsPath = `/staff/${accountType}/${accountId}/analytics-access/individual-stats${
    analyticsReportingMode ? `?analyticsReportingMode=${analyticsReportingMode}` : ''
  }`;

  let libraryBody: React.ReactNode;
  if (templatesQuery.isLoading) {
    libraryBody = <DianomiSpinner loading />;
  } else if (templatesQuery.isError) {
    libraryBody = <ErrorStateCard />;
  } else if (templates.length === 0) {
    libraryBody = <Typography color="text.secondary">No library templates available.</Typography>;
  } else {
    libraryBody = (
      <>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select assignable
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={selected.size === 0 || assignMutation.isPending}
            onClick={handleAssign}
          >
            Assign selected ({selected.size})
          </Button>
        </Stack>
        <Stack spacing={1}>
          {templates.map((template) => {
            const { assignability } = template;
            const disabled = !assignability.canAssign;
            const label = getAssignabilityLabel(assignability);

            const row = (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={selected.has(template.publicId)}
                  disabled={disabled}
                  onChange={() => toggle(template.publicId, assignability.canAssign)}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {template.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.statKey}
                    {label ? ` · ${label}` : ''}
                  </Typography>
                </Box>
              </Stack>
            );

            return (
              <Box
                key={template.publicId}
                sx={{
                  py: 1,
                  px: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                {!assignability.hasAccountLimit ? (
                  <Tooltip
                    title={
                      <>
                        Client has no access configured for this stat.{' '}
                        <MuiLink component={Link} to={individualStatsPath}>
                          Configure access
                        </MuiLink>
                      </>
                    }
                  >
                    {row}
                  </Tooltip>
                ) : (
                  row
                )}
              </Box>
            );
          })}
        </Stack>
      </>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Assign from library</Typography>
      <TextField
        select
        label="Filter by stat"
        size="small"
        value={statKeyFilter}
        onChange={(e) => handleStatKeyFilterChange(e.target.value)}
        sx={{ maxWidth: 280 }}
      >
        <MenuItem value="">All stats</MenuItem>
        {statKeyOptions.map((key) => (
          <MenuItem key={key} value={key}>
            {key}
          </MenuItem>
        ))}
      </TextField>
      {libraryBody}
    </Stack>
  );
}

function ParentAssignFromDefaultsSection({
  parentAccountId,
  childClientId,
  columnsPath,
}: {
  parentAccountId: string;
  childClientId: string;
  columnsPath: string;
}) {
  const [statKeyFilter, setStatKeyFilter] = React.useState('');
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const { setTimedNotification } = useNotifications();
  const assignMutation = useAssignParentChildReportTemplatesMutation();
  const { configQuery } = useStaffAnalyticsConfigMode();
  const analyticsReportingMode = configQuery?.analyticsReportingMode ?? null;

  const templatesQuery = useParentChildReportTemplatesQuery(
    parentAccountId,
    childClientId,
    statKeyFilter || undefined,
    analyticsReportingMode,
  );
  const allTemplatesQuery = useParentChildReportTemplatesQuery(
    parentAccountId,
    childClientId,
    undefined,
    analyticsReportingMode,
  );
  const templates = templatesQuery.data?.templates ?? [];
  const statKeyOptions = React.useMemo(
    () => [...new Set((allTemplatesQuery.data?.templates ?? []).map((t) => t.statKey))].sort(),
    [allTemplatesQuery.data?.templates],
  );

  const selectableIds = templates
    .filter((t) => t.assignability.canAssign)
    .map((t) => t.assignedPublicId);

  const handleStatKeyFilterChange = (value: string) => {
    setStatKeyFilter(value);
    setSelected(new Set());
  };

  const toggle = (assignedPublicId: string, canAssign: boolean) => {
    if (!canAssign) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(assignedPublicId)) next.delete(assignedPublicId);
      else next.add(assignedPublicId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected(new Set(selectableIds));
  };

  const handleAssign = () => {
    const assignedPublicIds = [...selected];
    if (assignedPublicIds.length === 0) return;
    assignMutation.mutate(
      { parentAccountId, childClientId, assignedPublicIds, analyticsReportingMode },
      {
        onSuccess: (data) => {
          setSelected(new Set());
          const summary = summarizeBulkTemplateResults(data.results, 'assign');
          setTimedNotification('success', summary, 6000);
          const failures = getBulkTemplateFailureMessages(data.results);
          if (failures.length > 0) {
            setTimedNotification('danger', failures.join(' '), 8000);
          }
        },
        onError: (err) => {
          setTimedNotification(
            'danger',
            err instanceof Error ? err.message : 'Assign failed',
            5000,
          );
        },
      },
    );
  };

  let pickerBody: React.ReactNode;
  if (templatesQuery.isLoading) {
    pickerBody = <DianomiSpinner loading />;
  } else if (templatesQuery.isError) {
    pickerBody = <ErrorStateCard />;
  } else if (templates.length === 0) {
    pickerBody = (
      <Typography color="text.secondary">
        No default reports assigned to your account yet. Ask Dianomi staff to assign templates from
        the library first.
      </Typography>
    );
  } else {
    pickerBody = (
      <>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select assignable
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={selected.size === 0 || assignMutation.isPending}
            onClick={handleAssign}
          >
            Assign selected ({selected.size})
          </Button>
        </Stack>
        <Stack spacing={1}>
          {templates.map((template) => {
            const { assignability } = template;
            const disabled = !assignability.canAssign;
            const label = getAssignabilityLabel(assignability);

            const row = (
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={selected.has(template.assignedPublicId)}
                  disabled={disabled}
                  onChange={() => toggle(template.assignedPublicId, assignability.canAssign)}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {templateDisplayName(template.name)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.statKey}
                    {label ? ` · ${label}` : ''}
                  </Typography>
                </Box>
              </Stack>
            );

            return (
              <Box
                key={template.assignedPublicId}
                sx={{
                  py: 1,
                  px: 1.5,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                {!assignability.hasAccountLimit ? (
                  <Tooltip
                    title={
                      <>
                        This child has no column access for this stat.{' '}
                        <MuiLink component={Link} to={columnsPath}>
                          Configure column access
                        </MuiLink>
                      </>
                    }
                  >
                    {row}
                  </Tooltip>
                ) : (
                  row
                )}
              </Box>
            );
          })}
        </Stack>
      </>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Assign from your default reports</Typography>
      <TextField
        select
        label="Filter by stat"
        size="small"
        value={statKeyFilter}
        onChange={(e) => handleStatKeyFilterChange(e.target.value)}
        sx={{ maxWidth: 280 }}
      >
        <MenuItem value="">All stats</MenuItem>
        {statKeyOptions.map((key) => (
          <MenuItem key={key} value={key}>
            {key}
          </MenuItem>
        ))}
      </TextField>
      {pickerBody}
    </Stack>
  );
}

function ParentAssignedToChildSection({
  parentAccountId,
  childClientId,
}: {
  parentAccountId: string;
  childClientId: string;
}) {
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [unassignDialogOpen, setUnassignDialogOpen] = React.useState(false);
  const { setTimedNotification } = useNotifications();
  const unassignMutation = useUnassignParentChildReportTemplatesMutation();
  const { configQuery } = useStaffAnalyticsConfigMode();
  const templatesQuery = useParentChildReportTemplatesQuery(
    parentAccountId,
    childClientId,
    undefined,
    configQuery?.analyticsReportingMode ?? null,
  );
  const assigned = (templatesQuery.data?.templates ?? []).flatMap((template) => {
    const { childAssignedPublicId } = template.assignability;
    if (!template.assignability.alreadyAssigned || !childAssignedPublicId) {
      return [];
    }
    return [{ ...template, childAssignedPublicId }];
  });

  const selectedTemplates = React.useMemo(
    () => assigned.filter((template) => selected.has(template.childAssignedPublicId)),
    [assigned, selected],
  );

  const unassignTargetLabel =
    selectedTemplates.length === 1
      ? `"${templateDisplayName(selectedTemplates[0].name)}"`
      : `${selectedTemplates.length} templates`;

  const toggle = (childAssignedPublicId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(childAssignedPublicId)) next.delete(childAssignedPublicId);
      else next.add(childAssignedPublicId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected(new Set(assigned.map((template) => template.childAssignedPublicId)));
  };

  const handleConfirmUnassign = () => {
    const assignedPublicIds = [...selected];
    if (assignedPublicIds.length === 0) return;
    unassignMutation.mutate(
      {
        parentAccountId,
        childClientId,
        assignedPublicIds,
        analyticsReportingMode: configQuery?.analyticsReportingMode ?? null,
      },
      {
        onSuccess: (data) => {
          setUnassignDialogOpen(false);
          setSelected(new Set());
          const summary = summarizeBulkTemplateResults(data.results, 'unassign');
          setTimedNotification('success', summary, 6000);
          const failures = getBulkTemplateFailureMessages(data.results);
          if (failures.length > 0) {
            setTimedNotification('danger', failures.join(' '), 8000);
          }
        },
        onError: (err) => {
          setTimedNotification(
            'danger',
            err instanceof Error ? err.message : 'Unassign failed',
            5000,
          );
        },
      },
    );
  };

  let assignedBody: React.ReactNode;
  if (templatesQuery.isLoading) {
    assignedBody = <DianomiSpinner loading />;
  } else if (templatesQuery.isError) {
    assignedBody = <ErrorStateCard />;
  } else if (assigned.length === 0) {
    assignedBody = (
      <Typography color="text.secondary">No templates assigned to this child yet.</Typography>
    );
  } else {
    assignedBody = (
      <>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select all
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={selected.size === 0 || unassignMutation.isPending}
            onClick={() => setUnassignDialogOpen(true)}
          >
            Unassign selected ({selected.size})
          </Button>
        </Stack>
        <Stack spacing={1}>
          {assigned.map((template) => (
            <Box
              key={template.childAssignedPublicId}
              sx={{
                py: 1,
                px: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={selected.has(template.childAssignedPublicId)}
                  onChange={() => toggle(template.childAssignedPublicId)}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {templateDisplayName(template.name)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.statKey}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          ))}
        </Stack>
      </>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Assigned to child</Typography>
      {assignedBody}
      <ActionConfirmDialog
        open={unassignDialogOpen}
        title={selectedTemplates.length === 1 ? 'Unassign template' : 'Unassign templates'}
        message={
          <ReportAccessLossWarning
            intro={<>Are you sure you want to unassign {unassignTargetLabel}?</>}
            lossDescription={
              <>
                The account will no longer be able to access{' '}
                {selectedTemplates.length === 1 ? 'this default report' : 'these default reports'}{' '}
                including:
              </>
            }
            footer="To restore access, assign the template again from your default reports."
          />
        }
        handleDialogClose={() => setUnassignDialogOpen(false)}
        handleDialogConfirm={handleConfirmUnassign}
        acceptText="Unassign"
        isSubmitting={unassignMutation.isPending}
      />
    </Stack>
  );
}

function AssignedTemplatesClientViews({
  accountType,
  accountId,
}: {
  accountType: 'client' | 'partner';
  accountId: string;
}) {
  const queryClient = useQueryClient();
  const { configQuery } = useStaffAnalyticsConfigMode();
  const authoringStatSet = configQuery?.analyticsReportingMode ?? null;
  const statSetSearch = authoringStatSet ? `?analyticsReportingMode=${authoringStatSet}` : '';
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [unassignDialogOpen, setUnassignDialogOpen] = React.useState(false);
  const { setTimedNotification } = useNotifications();
  const staffUnassignMutation = useUnassignStaffClientReportTemplatesMutation();
  const numericAccountId = Number(accountId);
  const viewsQuery = useQuery({
    ...getAnalyticsStatsViewsQueryOptions(
      numericAccountId,
      accountType,
      'authoring',
      authoringStatSet,
    ),
    enabled: Number.isFinite(numericAccountId) && numericAccountId > 0,
  });
  const assigned = (viewsQuery.data?.views ?? []).filter(isAssignedTemplate);
  const runBase = `/staff/${accountType}/${accountId}/analytics/stats`;

  const handlePrefetchRun = React.useCallback(
    (statKey: string, viewPublicId: string) => {
      if (!numericAccountId) return;
      prefetchQueryNonBlocking(
        queryClient,
        getAnalyticsStatsViewsQueryOptions(
          numericAccountId,
          accountType,
          'authoring',
          authoringStatSet,
        ),
      );
      prefetchQueryNonBlocking(
        queryClient,
        analyticsStatMetadataQueryOptions(numericAccountId, accountType, statKey, {
          mode: 'authoring',
          viewPublicId,
          authoringStatSet,
        }),
      );
    },
    [accountType, authoringStatSet, numericAccountId, queryClient],
  );

  const selectedViews = React.useMemo(
    () => assigned.filter((view) => selected.has(view.publicId)),
    [assigned, selected],
  );

  const unassignTargetLabel =
    selectedViews.length === 1
      ? `"${selectedViews[0].name ?? 'Untitled report'}"`
      : `${selectedViews.length} templates`;

  const toggle = (publicId: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(publicId)) next.delete(publicId);
      else next.add(publicId);
      return next;
    });
  };

  const handleSelectAll = () => {
    setSelected(new Set(assigned.map((view) => view.publicId)));
  };

  const handleConfirmUnassign = () => {
    const assignedPublicIds = [...selected];
    if (assignedPublicIds.length === 0) return;

    const onSuccess = (data: { results: Parameters<typeof summarizeBulkTemplateResults>[0] }) => {
      setUnassignDialogOpen(false);
      setSelected(new Set());
      const summary = summarizeBulkTemplateResults(data.results, 'unassign');
      setTimedNotification('success', summary, 6000);
      const failures = getBulkTemplateFailureMessages(data.results);
      if (failures.length > 0) {
        setTimedNotification('danger', failures.join(' '), 8000);
      }
    };
    const onError = (err: unknown) => {
      setTimedNotification('danger', err instanceof Error ? err.message : 'Unassign failed', 5000);
    };

    staffUnassignMutation.mutate(
      { accountType, accountId, assignedPublicIds, analyticsReportingMode: authoringStatSet },
      { onSuccess, onError },
    );
  };

  let assignedBody: React.ReactNode;
  if (viewsQuery.isLoading) {
    assignedBody = <DianomiSpinner loading />;
  } else if (viewsQuery.isError) {
    assignedBody = <ErrorStateCard />;
  } else if (assigned.length === 0) {
    assignedBody = (
      <Typography color="text.secondary">No templates assigned to this client yet.</Typography>
    );
  } else {
    assignedBody = (
      <>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" onClick={handleSelectAll}>
            Select all
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disabled={selected.size === 0 || staffUnassignMutation.isPending}
            onClick={() => setUnassignDialogOpen(true)}
          >
            Unassign selected ({selected.size})
          </Button>
        </Stack>
        <Stack spacing={1}>
          {assigned.map((view) => (
            <Box
              key={view.publicId}
              sx={{
                py: 1,
                px: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Checkbox
                  checked={selected.has(view.publicId)}
                  onChange={() => toggle(view.publicId)}
                />
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    {view.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {view.statKey}
                  </Typography>
                </Box>
              </Stack>
              <Button
                component={Link}
                to={`${runBase}/view/${view.publicId}${statSetSearch}`}
                size="small"
                onMouseEnter={() => handlePrefetchRun(view.statKey, view.publicId)}
                onFocus={() => handlePrefetchRun(view.statKey, view.publicId)}
              >
                Run
              </Button>
            </Box>
          ))}
        </Stack>
      </>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h3">Assigned to client</Typography>
      {assignedBody}
      <ActionConfirmDialog
        open={unassignDialogOpen}
        title={selectedViews.length === 1 ? 'Unassign template' : 'Unassign templates'}
        message={
          <ReportAccessLossWarning
            intro={<>Are you sure you want to unassign {unassignTargetLabel}?</>}
            lossDescription={
              <>
                The account will no longer be able to access{' '}
                {selectedViews.length === 1 ? 'this default report' : 'these default reports'}{' '}
                including:
              </>
            }
            footer="To restore access, assign the template again from the library."
          />
        }
        handleDialogClose={() => setUnassignDialogOpen(false)}
        handleDialogConfirm={handleConfirmUnassign}
        acceptText="Unassign"
        isSubmitting={staffUnassignMutation.isPending}
      />
    </Stack>
  );
}

export default function StaffClientReportTemplatesPage() {
  const statAccessMode = useStatAccessMode();
  const isParentMode = statAccessMode === 'parent';
  const { accountType, accountId, childClientId } = useParams<{
    accountType?: 'client' | 'partner';
    accountId?: string;
    childClientId?: string;
  }>();

  if (isParentMode) {
    if (!childClientId || !accountId) {
      return <ErrorStateCard />;
    }
    const columnsPath = `/client/${accountId}/account/child-accounts/${childClientId}/analytics-access/columns`;
    return (
      <Stack spacing={4}>
        <Alert severity="info">
          Assign default reports from your account onto this child. The child gets its own copy
          under Default reports. Only templates already assigned to you are available, and the child
          must have column access for that stat.
        </Alert>
        <ParentAssignFromDefaultsSection
          parentAccountId={accountId}
          childClientId={childClientId}
          columnsPath={columnsPath}
        />
        <ParentAssignedToChildSection parentAccountId={accountId} childClientId={childClientId} />
      </Stack>
    );
  }

  if (!accountType || !accountId || (accountType !== 'client' && accountType !== 'partner')) {
    return <ErrorStateCard />;
  }

  return (
    <Stack spacing={4}>
      <Alert severity="info">
        Assign platform templates to this client. Assigned copies appear under Default reports in
        client analytics.
      </Alert>
      <StaffAssignFromLibrarySection accountType={accountType} accountId={accountId} />
      <AssignedTemplatesClientViews accountType={accountType} accountId={accountId} />
    </Stack>
  );
}
